import { useAuthContext } from '@caterinx/api-client';
import type { SupplierStatus } from '@caterinx/shared-types';
import { Alert, AlertDescription, AlertTitle } from '@caterinx/ui/components/ui/alert';
import { Button } from '@caterinx/ui/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@caterinx/ui/components/ui/card';
import { Skeleton } from '@caterinx/ui/components/ui/skeleton';
import { Wordmark } from '@caterinx/ui/components/ui/wordmark';
import { useQuery } from '@tanstack/react-query';
import { Building2, ClipboardList, Receipt, Users } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import { api } from '../lib/api.js';
import { fetchMyProfile } from '../lib/supplier.js';

interface MeResponse {
  userId: string;
  email: string;
  name: string;
  role: string;
  supplier: {
    supplierId: string;
    companyName: string;
    status: SupplierStatus;
    verifiedAt: string | null;
  } | null;
}

/**
 * Die Startseite nach der Anmeldung.
 *
 * Sie beantwortet genau eine Frage: **Was muss ich jetzt tun?** Bei einem frisch angelegten
 * Betrieb ist das „Profil vervollständigen", bei einem eingereichten „warten", bei einem
 * freigeschalteten „auf Ausschreibungen reagieren" (ab Phase 5). Deshalb steht der
 * Verifizierungsstand oben und nicht in einem Untermenü — ein Lieferant, der auf die Freigabe
 * wartet, soll das sofort sehen und nicht rätseln, warum keine Anfragen kommen.
 */
export function PortalPage() {
  const { logout } = useAuthContext();
  const navigate = useNavigate();

  const { data, isPending, isError } = useQuery({
    queryKey: ['me'],
    queryFn: async () => (await api.get<MeResponse>('/auth/me')).data,
  });

  /**
   * Das vollständige Profil nur laden, wenn überhaupt ein Betrieb dranhängt — sonst
   * liefe die Abfrage garantiert in einen 403 und der Nutzer sähe einen Fehler, wo eine
   * Erklärung stehen müsste.
   */
  const { data: profile } = useQuery({
    queryKey: ['supplier-profile'],
    queryFn: fetchMyProfile,
    enabled: Boolean(data?.supplier),
  });

  const handleLogout = (): void => {
    logout();
    navigate('/login', { replace: true });
  };

  const missing = profile?.missingFields ?? [];

  return (
    <main className="mx-auto max-w-3xl space-y-6 p-4 sm:p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="flex flex-wrap items-center gap-x-3 gap-y-1 text-2xl font-semibold">
          <Wordmark className="w-40" />
          <span>Lieferantenportal</span>
        </h1>
        <Button variant="outline" onClick={handleLogout}>
          Abmelden
        </Button>
      </header>

      {isPending && <Skeleton className="h-32 w-full" />}

      {isError && (
        <Alert variant="destructive">
          <AlertTitle>Profil konnte nicht geladen werden</AlertTitle>
          <AlertDescription>Bitte laden Sie die Seite neu.</AlertDescription>
        </Alert>
      )}

      {data && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>{data.supplier?.companyName || 'Ihr Betrieb'}</CardTitle>
              <CardDescription>
                Angemeldet als {data.email}
                {data.name ? ` (${data.name})` : ''}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <SupplierStatusNotice status={data.supplier?.status} reason={profile?.statusReason ?? null} />

              {missing.length > 0 && (
                <Alert>
                  <AlertTitle>Ihr Profil ist noch nicht vollständig</AlertTitle>
                  <AlertDescription>
                    <ul className="mt-2 list-disc space-y-1 pl-5">
                      {missing.map((gap) => (
                        <li key={gap.field}>{gap.label}</li>
                      ))}
                    </ul>
                    <Button asChild className="mt-4 min-h-11">
                      <Link to="/profil">Jetzt vervollständigen</Link>
                    </Button>
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>

          <div className="grid gap-4 sm:grid-cols-2">
            <NavCard
              to="/ausschreibungen"
              icon={<ClipboardList className="h-5 w-5" aria-hidden="true" />}
              title="Ausschreibungen"
              description="Anfragen ansehen und Angebote abgeben"
            />
            <NavCard
              to="/profil"
              icon={<Building2 className="h-5 w-5" aria-hidden="true" />}
              title="Betriebsprofil"
              description="Leistungen, Liefergebiete, Nachweise"
            />
            <NavCard
              to="/provisionen"
              icon={<Receipt className="h-5 w-5" aria-hidden="true" />}
              title="Provisionen"
              description="Rechnungen und offene Beträge"
            />
            <NavCard
              to="/team"
              icon={<Users className="h-5 w-5" aria-hidden="true" />}
              title="Mitarbeiter"
              description="Kollegen einladen und verwalten"
            />
          </div>
        </>
      )}
    </main>
  );
}

function NavCard({
  to,
  icon,
  title,
  description,
}: {
  to: string;
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <Link
      to={to}
      className="hover:border-primary focus-visible:ring-ring block rounded-lg border p-4 transition-colors focus-visible:ring-2 focus-visible:outline-none"
    >
      <div className="flex items-center gap-2 font-medium">
        {icon}
        {title}
      </div>
      <p className="text-muted-foreground mt-1 text-sm">{description}</p>
    </Link>
  );
}

function SupplierStatusNotice({ status, reason }: { status?: SupplierStatus; reason: string | null }) {
  switch (status) {
    case 'verified':
      return (
        <Alert>
          <AlertTitle>Freigeschaltet</AlertTitle>
          <AlertDescription>Sie erhalten passende Ausschreibungen per E-Mail.</AlertDescription>
        </Alert>
      );
    case 'in_review':
      return (
        <Alert>
          <AlertTitle>Prüfung läuft</AlertTitle>
          <AlertDescription>Wir melden uns, sobald Ihre Unterlagen geprüft sind.</AlertDescription>
        </Alert>
      );
    case 'suspended':
      return (
        <Alert variant="destructive">
          <AlertTitle>Konto gesperrt</AlertTitle>
          <AlertDescription>{reason ?? 'Bitte wenden Sie sich an angebote@caterinx.com.'}</AlertDescription>
        </Alert>
      );
    case 'rejected':
      return (
        <Alert variant="destructive">
          <AlertTitle>Anmeldung abgelehnt</AlertTitle>
          <AlertDescription>
            {reason ?? 'Bitte wenden Sie sich an angebote@caterinx.com.'} Sie können Ihre Angaben ergänzen und erneut
            einreichen.
          </AlertDescription>
        </Alert>
      );
    default:
      return (
        <Alert>
          <AlertTitle>Verifizierung ausstehend</AlertTitle>
          <AlertDescription>
            Bevor Sie Ausschreibungen erhalten, prüfen wir Handelsregister, USt-IdNr. und den Nachweis zur
            Lebensmittelhygiene. Vervollständigen Sie dazu bitte Ihr Profil.
          </AlertDescription>
        </Alert>
      );
  }
}
